const API_BASE_URL = '/api/auth';

// Configuration: prefer httpOnly cookies if available, fallback to localStorage
const USE_HTTP_ONLY_COOKIES = true; // Set to false to use localStorage only

class AuthService {
  constructor() {
    this.useCookies = USE_HTTP_ONLY_COOKIES;
    // Shared promise for concurrent refresh attempts - prevents race conditions
    this._refreshPromise = null;
  }

  // Helper to get token from cookie or localStorage
  _getTokenFromStorage(tokenName) {
    if (this.useCookies) {
      // Try to read from cookie (note: httpOnly cookies cannot be read from JavaScript)
      // If using httpOnly cookies, this will return null and we rely on server-side auth
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === tokenName && value) {
          return decodeURIComponent(value);
        }
      }
      // If using httpOnly cookies, we can't read them from JS
      // Return null - authentication will be handled server-side via cookies
      return null;
    }
    // Fallback to localStorage
    return localStorage.getItem(tokenName);
  }

  // Helper to set token in storage
  _setTokenInStorage(tokenName, token) {
    if (this.useCookies) {
      // Token will be set by server via httpOnly cookie
      // We can't set httpOnly cookies from JavaScript, so we rely on server
      // For non-httpOnly cookies, we could set them here, but we prefer httpOnly
      return;
    }
    localStorage.setItem(tokenName, token);
  }

  // Helper to remove token from storage
  _removeTokenFromStorage(tokenName) {
    if (this.useCookies) {
      // Clear cookie (server should handle this, but we can try)
      document.cookie = `${tokenName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      return;
    }
    localStorage.removeItem(tokenName);
  }
  async register(userData) {
    const response = await fetch(`${API_BASE_URL}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    const data = await response.json();
    
    if (!response.ok) {
      // Handle different error formats
      let errorMessage = 'Registration failed';
      
      if (data.errors && Array.isArray(data.errors)) {
        // Multiple errors (like validation errors)
        errorMessage = data.errors.join('. ');
      } else if (data.message) {
        // Single error message
        errorMessage = data.message;
      }
      
      throw new Error(errorMessage);
    }
    
    return data;
  }

  async login(credentials) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Signal to server that we prefer httpOnly cookies
    if (this.useCookies) {
      headers['X-Use-HttpOnly-Cookies'] = 'true';
    }

    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: headers,
      credentials: 'include', // Include cookies in request
      body: JSON.stringify(credentials),
    });

    // Check content type before parsing JSON
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      // If not JSON, read as text and throw error
      const text = await response.text();
      throw new Error(text || 'Login failed');
    }
    
    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }
    
    // Check if MFA is required
    if (data.code === 'mfa_required' || data.requiresMfa) {
      return { requiresMfa: true, tempToken: data.tempToken, ...data };
    }
    
    // Store tokens based on storage method
    if (data.useCookies) {
      // Tokens are in httpOnly cookies, don't store in localStorage
      // Only store user info
      localStorage.setItem('user', JSON.stringify(data.user));
      this.useCookies = true; // Update preference
    } else {
      // Store tokens in localStorage
      this._setTokenInStorage('authToken', data.token);
      if (data.refreshToken) {
        this._setTokenInStorage('refreshToken', data.refreshToken);
      }
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    
    // Reset AI selections on fresh login so server defaults take effect
    try {
      localStorage.removeItem('ai_provider');
      localStorage.removeItem('ai_model');
      localStorage.removeItem('ai_defaultMaxTokens');
      localStorage.removeItem('ai_chatMode');
    } catch {}
    
    return data;
  }

  async loginWithMfa(tempToken, mfaCode) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Signal to server that we prefer httpOnly cookies
    if (this.useCookies) {
      headers['X-Use-HttpOnly-Cookies'] = 'true';
    }

    const response = await fetch(`${API_BASE_URL}/login-mfa`, {
      method: 'POST',
      headers: headers,
      credentials: 'include', // Include cookies in request
      body: JSON.stringify({ tempToken, mfaCode }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'MFA verification failed');
    }
    
    // Store tokens based on storage method
    if (data.useCookies) {
      // Tokens are in httpOnly cookies, don't store in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      this.useCookies = true;
    } else {
      // Store tokens in localStorage
      this._setTokenInStorage('authToken', data.token);
      if (data.refreshToken) {
        this._setTokenInStorage('refreshToken', data.refreshToken);
      }
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    
    // Reset AI selections on fresh login so server defaults take effect
    try {
      localStorage.removeItem('ai_provider');
      localStorage.removeItem('ai_model');
      localStorage.removeItem('ai_defaultMaxTokens');
      localStorage.removeItem('ai_chatMode');
    } catch {}
    
    return data;
  }

  async logout(sessionExpired = false) {
    // Check if user is authenticated (has user in localStorage) instead of checking token
    // When using httpOnly cookies, getToken() returns empty string, so we need to check user instead
    const user = this.getCurrentUser();
    const token = this.getToken();
    
    // Clear local state FIRST to prevent race conditions and stop polling
    this._removeTokenFromStorage('authToken');
    this._removeTokenFromStorage('refreshToken');
    localStorage.removeItem('user');
    
    // Set a flag to prevent any pending getUserInfo calls from triggering logout
    // This prevents the logout loop issue
    try {
      sessionStorage.setItem('logout_in_progress', '1');
    } catch {}
    
    // Clear cached AI selections so server defaults apply on next login
    try {
      localStorage.removeItem('ai_provider');
      localStorage.removeItem('ai_model');
      localStorage.removeItem('ai_defaultMaxTokens');
      localStorage.removeItem('ai_chatMode');
    } catch {}
    
    // Call server to revoke tokens and clear cookies
    // Always call if user exists OR if we have a token (covers both cookie and localStorage scenarios)
    if (user || token) {
      try {
        const headers = {};
        // Only add Authorization header if we have a token (not using httpOnly cookies)
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_BASE_URL}/logout`, {
          method: 'POST',
          headers: headers,
          credentials: 'include', // Include cookies (required for httpOnly cookie auth)
        });
        
        // Don't treat 401 from logout endpoint as session expired - it's normal during logout
        // Just continue with logout process even if the endpoint returns 401
        if (!response.ok && response.status !== 401) {
          console.warn('Logout API call returned error:', response.status);
        }
      } catch (error) {
        console.error('Logout API call failed:', error);
        // Continue with logout even if API call fails
      }
    }
    
    // Force clear any remaining cookies (for non-httpOnly cookies)
    // Note: This won't work for httpOnly cookies, but server should have cleared them
    document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost;';
    document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost;';
    document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Clear the logout flag after a short delay (allows redirect to complete)
    setTimeout(() => {
      try {
        sessionStorage.removeItem('logout_in_progress');
      } catch {}
    }, 5000);
    
    // Only redirect with sessionExpired flag if explicitly passed as true
    // This prevents regular logout from showing session expired toast
    if (sessionExpired && !window.location.pathname.includes('/login')) {
      window.location.href = '/login?sessionExpired=true';
    }
  }

  async refreshToken() {
    const refreshToken = this._getTokenFromStorage('refreshToken');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.useCookies) {
      headers['X-Use-HttpOnly-Cookies'] = 'true';
    } else {
      headers['X-Refresh-Token'] = refreshToken;
    }

    const response = await fetch(`${API_BASE_URL}/refresh`, {
      method: 'POST',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify({ refreshToken: this.useCookies ? null : refreshToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Token refresh failed');
    }

    const data = await response.json();
    
    // Store tokens based on storage method
    if (data.useCookies) {
      // Tokens are in httpOnly cookies
      this.useCookies = true;
    } else {
      // Store new tokens
      this._setTokenInStorage('authToken', data.token);
      if (data.refreshToken) {
        this._setTokenInStorage('refreshToken', data.refreshToken);
      }
    }
    
    return data;
  }

  async forgotPassword(email) {
    const response = await fetch(`${API_BASE_URL}/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      // Try to get error message from JSON, fallback to status text
      let errorMessage = 'Password reset request failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (jsonError) {
        // If response is not JSON (e.g., HTML error page), use status text
        errorMessage = response.statusText || `Request failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return data;
  }

  async resetPassword(resetData) {
    const response = await fetch(`${API_BASE_URL}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resetData),
    });

    if (!response.ok) {
      // Try to get error message from JSON, fallback to status text
      let errorMessage = 'Password reset failed';
      try {
        const errorData = await response.json();
        
        // Handle Identity validation errors (array of error objects)
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.map(e => e.description || e.code || e).join('. ');
        } 
        // Handle ASP.NET ModelState errors
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        // Handle single error object
        else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } catch (jsonError) {
        // If response is not JSON (e.g., HTML error page), use status text
        errorMessage = response.statusText || `Request failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return data;
  }

  async changePassword(changeData) {
    const response = await this.authenticatedFetch(`${API_BASE_URL}/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(changeData),
    });

    if (!response.ok) {
      let errorMessage = 'Password change failed';
      try {
        const errorData = await response.json();
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.map(e => e.description || e.code || e).join('. ');
        } 
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } catch (jsonError) {
        errorMessage = response.statusText || `Request failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return data;
  }

  async resendConfirmation(email) {
    const response = await fetch(`${API_BASE_URL}/resend-confirmation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Resend confirmation failed');
    }
    
    return data;
  }

  async getUserInfo() {
    const headers = this.getAuthHeaders();
    
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/user-info`, {
        headers: headers,
        credentials: 'include', // Include cookies for httpOnly cookie auth
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // If 401, wait for middleware refresh to complete, then retry multiple times
      // Middleware refresh can take 2-3 seconds, so we need to wait longer
      if (response.status === 401) {
        // Wait for middleware to potentially refresh the token (middleware refresh takes ~2 seconds)
        await new Promise(resolve => setTimeout(resolve, 2500));
        
        // Retry with updated headers (middleware should have refreshed by now)
        const retryController = new AbortController();
        const retryTimeoutId = setTimeout(() => retryController.abort(), 10000);
        try {
          response = await fetch(`${API_BASE_URL}/user-info`, {
            headers: this.getAuthHeaders(),
            credentials: 'include',
            signal: retryController.signal,
          });
          clearTimeout(retryTimeoutId);
        } catch (err) {
          clearTimeout(retryTimeoutId);
          if (err.name === 'AbortError') {
            throw new Error('Request timeout. Please try again.');
          }
          throw err;
        }
        
        // If still 401, wait a bit more and try once more (in case refresh is still in progress)
        if (response.status === 401) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const finalController = new AbortController();
          const finalTimeoutId = setTimeout(() => finalController.abort(), 10000);
          try {
            response = await fetch(`${API_BASE_URL}/user-info`, {
              headers: this.getAuthHeaders(),
              credentials: 'include',
              signal: finalController.signal,
            });
            clearTimeout(finalTimeoutId);
          } catch (err) {
            clearTimeout(finalTimeoutId);
            if (err.name === 'AbortError') {
              throw new Error('Request timeout. Please try again.');
            }
            throw err;
          }
        }
        
        // If still 401 after retries, then logout
        // But only if we're not already on the login page (to prevent loops after login)
        // Also check if logout is already in progress to prevent logout loops
        if (response.status === 401) {
          // Check if logout is already in progress
          const logoutInProgress = sessionStorage.getItem('logout_in_progress') === '1';
          
          // Don't auto-logout if:
          // 1. We're already on login page (prevents redirect loop)
          // 2. Logout is already in progress (prevents logout loop)
          // 3. We're on a public page (reset-password, etc.)
          const isPublicPage = () => {
            const path = window.location.pathname || '';
            const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/confirm-email', '/app-info', '/docs', '/pricing', '/'];
            return publicPaths.some(p => path === p || path.startsWith(p + '/'));
          };
          
          if (!window.location.pathname.includes('/login') && !logoutInProgress && !isPublicPage()) {
            this.logout(true); // Pass true to indicate session expired
          }
          throw new Error('Session expired. Please login again.');
        }
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error('Request timeout. Please try again.');
      }
      // Re-throw other errors
      throw err;
    }

    let data = {};
    try {
      const text = await response.text();
      if (text) {
        data = JSON.parse(text);
      }
    } catch (e) {
      // If JSON parsing fails, data remains empty object
    }
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to get user info');
    }
    
    return data;
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getToken() {
    const token = this._getTokenFromStorage('authToken');
    // If using cookies and token is null, return empty string instead of null
    // This prevents components from sending "Bearer null" in headers
    // The middleware will handle cookie-based auth
    if (this.useCookies && !token) {
      return '';
    }
    return token;
  }

  isAuthenticated() {
    // Check if we have user info (user info is cleared on logout)
    // If using httpOnly cookies, token might not be readable from JS, but user will be in localStorage
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }
    
    // If using cookies, assume authenticated if user exists (server handles cookie auth)
    if (this.useCookies) {
      return true;
    }
    
    // For localStorage, check both token and user
    const token = this.getToken();
    return !!token;
  }

  // Helper method to set auth header for API calls
  getAuthHeaders() {
    // If using httpOnly cookies, don't set Authorization header (cookies are sent automatically)
    if (this.useCookies) {
      return {};
    }
    
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Helper method to make authenticated API calls with automatic token refresh
  async authenticatedFetch(url, options = {}) {
    let response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      credentials: 'include',
    });

    // If token expired, try to refresh (only if not using httpOnly cookies - middleware handles it)
    if (response.status === 401 && !this.useCookies) {
      try {
        await this.refreshToken();
        // Retry original request with new token
        response = await fetch(url, {
          ...options,
          headers: {
            ...this.getAuthHeaders(),
            ...options.headers,
          },
          credentials: 'include',
        });
      } catch (refreshError) {
        // Refresh failed, user needs to login again
        // But only if we're not already on the login page (to prevent loops after login)
        // Also check if logout is already in progress to prevent logout loops
        const logoutInProgress = sessionStorage.getItem('logout_in_progress') === '1';
        const isPublicPage = () => {
          const path = window.location.pathname || '';
          const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/confirm-email', '/app-info', '/docs', '/pricing', '/'];
          return publicPaths.some(p => path === p || path.startsWith(p + '/'));
        };
        
        if (!window.location.pathname.includes('/login') && !logoutInProgress && !isPublicPage()) {
          this.logout(true); // Pass true to indicate session expired
        }
        throw new Error('Session expired. Please login again.');
      }
    }
    // If using httpOnly cookies and we get 401, wait for middleware refresh, then retry multiple times
    else if (response.status === 401 && this.useCookies) {
      // If there's already a refresh in progress, wait for it to complete
      // This prevents multiple concurrent requests from all trying to refresh simultaneously
      if (this._refreshPromise) {
        try {
          await this._refreshPromise;
          // Small delay to ensure cookie is fully updated (especially important when DevTools is open)
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (refreshError) {
          // Refresh failed, but continue to retry the original request
        }
      } else {
        // Create a shared refresh promise that all concurrent requests will wait for
        // The middleware handles the actual refresh, we just need to wait for it
        this._refreshPromise = new Promise(async (resolve) => {
          // Wait for middleware to potentially refresh the token (middleware refresh takes ~2 seconds)
          await new Promise(resolve => setTimeout(resolve, 2500));
          // Small delay to ensure cookie is fully updated (especially important when DevTools is open)
          await new Promise(resolve => setTimeout(resolve, 200));
          // Clear the promise so future requests can create a new one if needed
          this._refreshPromise = null;
          resolve();
        });
        
        try {
          await this._refreshPromise;
        } catch (refreshError) {
          this._refreshPromise = null;
        }
      }
      
      // Retry with updated headers (middleware should have refreshed by now)
      response = await fetch(url, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
        credentials: 'include',
      });
      
      // If still 401, wait a bit more and try once more (in case refresh is still in progress)
      if (response.status === 401) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        response = await fetch(url, {
          ...options,
          headers: {
            ...this.getAuthHeaders(),
            ...options.headers,
          },
          credentials: 'include',
        });
      }
      
      // If still 401 after retries, then logout
      // But only if we're not already on the login page (to prevent loops after login)
      // Also check if logout is already in progress to prevent logout loops
      if (response.status === 401) {
        const logoutInProgress = sessionStorage.getItem('logout_in_progress') === '1';
        const isPublicPage = () => {
          const path = window.location.pathname || '';
          const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/confirm-email', '/app-info', '/docs', '/pricing', '/'];
          return publicPaths.some(p => path === p || path.startsWith(p + '/'));
        };
        
        if (!window.location.pathname.includes('/login') && !logoutInProgress && !isPublicPage()) {
          this.logout(true); // Pass true to indicate session expired
        }
        throw new Error('Session expired. Please login again.');
      }
    }

    return response;
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance; 